import React from 'react'
import styles from './OfferThirdPage.module.scss'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import GeneralContractInfoSubmitForm from './GeneralContractInfoSubmitForm'
import DomesticContractInfoSubmitForm from './DomesticContractInfoSubmitForm'
import OverseasContractInfoSubmitForm from './OverseasContractInfoSubmitForm'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { Tooltip } from 'react-tooltip'
import ProDisplay from '../../../components/ProDisplay/ProDisplay'
import CaddieDisplay from '../../../components/CaddieDisplay/CaddieDisplay'
import { useStateMachine } from 'little-state-machine'
import { updateStore, storeInitState } from '../../../store'
import { XMarkIcon } from '@heroicons/react/24/solid'
import { usePreferredContract } from '../../../hooks/contract'
import { useUser } from '../../../hooks/user'
import { useTours } from '../../../hooks/tournament'
import dayjs from 'dayjs'

const OfferThirdPage = () => {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const { actions, state } = useStateMachine({ updateStore })

	// URL 파라미터에서 사용자 타입 확인
	const userType = searchParams.get('by') || 'pro'
	const isCaddieView = userType === 'caddie'

	// useUser 훅으로 사용자 정보 가져오기
	const { data: currentUser } = useUser()
	const userAssociation = currentUser?.player_type || null

	// usePreferredContract 훅 사용
	const { data: preferredContract } = usePreferredContract()

	// useTours 훅 사용 (실패·빈 응답 시에도 fetch 완료 후에는 이전 단계 store를 반영해야 함)
	const { data: toursData, isFetched: toursQueryFetched } = useTours()

	// 전체 투어 목록을 배열로 변환 (domestic + international)
	const allTours = useMemo(() => {
		if (!toursData) return []
		const domestic = toursData.domestic || []
		const international = toursData.international?.tours
			? Object.values(toursData.international.tours).flat()
			: []
		return [...domestic, ...international]
	}, [toursData])

	// 파생 상태 계산
	const hasOverSeasPreferredContract = useMemo(
		() => !!preferredContract?.overseasContract,
		[preferredContract],
	)
	const hasDomesticPreferredContract = useMemo(
		() => !!preferredContract?.domesticContract,
		[preferredContract],
	)

	const [informationDisplayOption, setInformationDisplayOption] = useState(null)
	const [formValidations, setFormValidations] = useState({
		general: false,
		domestic: false,
		overseas: false,
	})

	// 폼 refs
	const generalFormRef = useRef()
	const domesticFormRef = useRef()
	const overseasFormRef = useRef()
	const [informationForDisplay, setInformationForDisplay] = useState({
		contractType: null,
		selectedTours: null,
		contractStartDate: null,
		contractEndDate: null,
		periodType: null,
		isTourSelected: null,
		currentYear: null,
		isOverseasContract: false,
		isDomesticContract: false,
		isGeneralContract: false,
		isShortContract: false,
		isLongContract: false,
		selectedToursData: [],
	})
	const [offerPeriodType, setOfferPeriodType] = useState(null)

	useEffect(() => {
		if (
			informationForDisplay.selectedToursData &&
			informationForDisplay.selectedToursData.length > 0
		) {
			// 지역별 계약 타입 설정
			informationForDisplay.selectedToursData.forEach((tour) => {
				if (tour.region === 'DOMESTIC') {
					setInformationForDisplay((prev) => ({
						...prev,
						isDomesticContract: true,
					}))
				} else if (tour.region === 'INTERNATIONAL') {
					setInformationForDisplay((prev) => ({
						...prev,
						isOverseasContract: true,
					}))
				}
			})
		}
	}, [informationForDisplay.selectedToursData])

	// validation 상태를 상위 컴포넌트로 전달
	const handleValidationChange = useCallback((formType, isValid) => {
		console.log(`Validation changed for ${formType}:`, isValid)
		setFormValidations((prev) => {
			const newState = {
				...prev,
				[formType]: isValid,
			}
			console.log('New formValidations state:', newState)
			return newState
		})
	}, [])

	const setDataFromPreviousPage = async () => {
		// store에서 데이터 가져오기
		const offerType = state.offerInput?.offerType
		const tourIds = state.offerInput?.tourIds || []
		const termType = state.offerInput?.termType || ''
		const currentYear = 2026

		// 스토어 TermType: first_half | second_half | full_year → UI용 periodType (first-half / last-half)
		// 주의: second_half → 'second-half'가 아니라 기존 UI 분기가 'last-half'를 기대함
		const periodType = (() => {
			if (termType === 'full_year') return 'first-half,last-half'
			if (termType === 'first_half') return 'first-half'
			if (termType === 'second_half') return 'last-half'
			if (!termType) return ''
			const dashed = String(termType).replace(/_/g, '-')
			return dashed === 'second-half' ? 'last-half' : dashed
		})()
		setOfferPeriodType(periodType)

		console.log('계약 타입:', offerType)
		console.log('선택된 투어:', tourIds)
		// console.log('기간 타입:', periodType)

		if (offerType === 'period' && tourIds.length === 0) {
			const isGeneralContract = true
			console.log('선택된 투어가 없습니다.')

			let endDate = ''
			let displayOption = ''

			if (periodType.includes('last-half') && periodType.includes('first-half')) {
				console.log('상하반기 선택됨')
				endDate = `${currentYear}-12-31`
				displayOption = 'period-no-tour-first-half-last-half'
			} else if (periodType.includes('first-half')) {
				console.log('상반기 선택됨')
				endDate = `${currentYear}-06-30`
				displayOption = 'period-no-tour-first-half'
			} else if (periodType.includes('last-half')) {
				console.log('하반기 선택됨')
				endDate = `${currentYear}-12-31`
				displayOption = 'period-no-tour-last-half'
			}

			// termType 누락·예외 값이면 위 분기를 못 타 displayOption이 비어 화면이 하얗게 됨
			if (!displayOption) {
				displayOption = 'period-no-tour-first-half-last-half'
				endDate = `${currentYear}-12-31`
			}

			setInformationForDisplay({
				contractType: offerType,
				selectedTours: tourIds,
				contractStartDate: '계약일',
				contractEndDate: endDate,
				periodType: periodType,
				isTourSelected: false,
				currentYear: currentYear,
				isGeneralContract: isGeneralContract,
			})

			setInformationDisplayOption(displayOption)
			console.log('설정된 정보:', {
				contractType: offerType,
				selectedTours: tourIds,
				contractStartDate: '계약일',
				contractEndDate: endDate,
				periodType: periodType,
				isTourSelected: false,
				currentYear: currentYear,
			})
			console.log('설정할 displayOption : ', displayOption)
		} else if (offerType === 'period' && tourIds.length > 0) {
			const isGeneralContract = true
			try {
				const selectedTours = allTours.filter((tour) => tourIds.includes(tour.id))
				const selectedToursData = selectedTours.map((tour) => {
					const startDate = tour.started_at || tour.startedAt
					const endDate = tour.ended_at || tour.endedAt
					return {
						id: tour.id,
						name: tour.name,
						startDate: startDate ? dayjs(startDate).format('YYYY-MM-DD') : '',
						endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : '',
						region: tour.region,
						category: tour.tour_category || tour.TourCategory,
					}
				})
				console.log('selectedToursData : ', selectedToursData)
				const displayOption = 'period-tour-selected'
				setInformationDisplayOption(displayOption)

				// 지역별 계약 타입 설정
				let isDomesticContract = false
				let isOverseasContract = false
				selectedToursData.forEach((tour) => {
					if (tour.region === 'DOMESTIC') {
						isDomesticContract = true
					} else if (tour.region === 'INTERNATIONAL') {
						isOverseasContract = true
					}
				})

				setInformationForDisplay((prev) => ({
					...prev,
					isGeneralContract: isGeneralContract,
					selectedToursData: selectedToursData,
					isDomesticContract: isDomesticContract,
					isOverseasContract: isOverseasContract,
				}))
			} catch (err) {
				console.error('데이터 가져오기 실패:', err)
				console.error('에러 메시지:', err.message || '데이터를 가져오는데 실패했습니다.')
			}
		} else if (offerType === 'short') {
			const isShortContract = true
			try {
				const selectedTours = allTours.filter((tour) => tourIds.includes(tour.id))
				const selectedToursData = selectedTours.map((tour) => {
					const startDate = tour.started_at || tour.startedAt
					const endDate = tour.ended_at || tour.endedAt
					return {
						id: tour.id,
						name: tour.name,
						startDate: startDate ? dayjs(startDate).format('YYYY-MM-DD') : '',
						endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : '',
						region: tour.region,
						category: tour.tour_category || tour.TourCategory,
					}
				})
				console.log('selectedToursData : ', selectedToursData)
				const displayOption = 'short-contract'
				setInformationDisplayOption(displayOption)

				// 지역별 계약 타입 설정
				let isDomesticContract = false
				let isOverseasContract = false
				selectedToursData.forEach((tour) => {
					if (tour.region === 'DOMESTIC') {
						isDomesticContract = true
					} else if (tour.region === 'INTERNATIONAL') {
						isOverseasContract = true
					}
				})

				setInformationForDisplay((prev) => ({
					...prev,
					isShortContract: isShortContract,
					selectedToursData: selectedToursData,
					isDomesticContract: isDomesticContract,
					isOverseasContract: isOverseasContract,
				}))
			} catch (err) {
				console.error('데이터 가져오기 실패:', err)
				console.error('에러 메시지:', err.message || '데이터를 가져오는데 실패했습니다.')
			}
		} else if (offerType === 'long') {
			const isLongContract = true
			try {
				const selectedTours = allTours.filter((tour) => tourIds.includes(tour.id))
				const selectedToursData = selectedTours.map((tour) => {
					const startDate = tour.started_at || tour.startedAt
					const endDate = tour.ended_at || tour.endedAt
					return {
						id: tour.id,
						name: tour.name,
						startDate: startDate ? dayjs(startDate).format('YYYY-MM-DD') : '',
						endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : '',
						region: tour.region,
						category: tour.tour_category || tour.TourCategory,
					}
				})
				console.log('selectedToursData : ', selectedToursData)
				const displayOption = 'long-contract'
				setInformationDisplayOption(displayOption)

				// 지역별 계약 타입 설정
				let isDomesticContract = false
				let isOverseasContract = false
				selectedToursData.forEach((tour) => {
					if (tour.region === 'DOMESTIC') {
						isDomesticContract = true
					} else if (tour.region === 'INTERNATIONAL') {
						isOverseasContract = true
					}
				})

				setInformationForDisplay((prev) => ({
					...prev,
					isLongContract: isLongContract,
					selectedToursData: selectedToursData,
					isDomesticContract: isDomesticContract,
					isOverseasContract: isOverseasContract,
				}))
			} catch (err) {
				console.error('데이터 가져오기 실패:', err)
				console.error('에러 메시지:', err.message || '데이터를 가져오는데 실패했습니다.')
			}
		}
	}

	useEffect(() => {
		const initializeData = async () => {
			// toursData만 보면 API 오류/누락 시 영원히 undefined → 약관 단계가 비어 보임.
			// 투어 목록 fetch가 끝난 뒤(성공·실패 무관)에는 store 기준으로 UI를 채운다.
			if (allTours.length > 0 || toursQueryFetched) {
				await setDataFromPreviousPage()
			}
		}
		initializeData()
	}, [allTours, toursQueryFetched])

	// informationDisplayOption 상태 변화 감지
	useEffect(() => {
		console.log('informationDisplayOption 상태 업데이트됨:', informationDisplayOption)
	}, [informationDisplayOption])

	const prepareDataForSubmit = () => {
		console.log('prepareDataForSubmit')
		const contract = {}

		// 각 폼의 데이터 수집 및 변환
		if (informationForDisplay.isGeneralContract) {
			// 기간 계약 (termContract)
			const generalData = generalFormRef.current?.getFormData?.()
			if (generalData) {
				contract.termContract = {
					isSalaryTaxIncluded: generalData.applyTax === 'include',
					payrollCycles: 'perGame',
					salaryAmountForPeriod: Number(generalData.periodSalary || 0),
					isOverseasIncluded: generalData.overseasTour || false,
					expenseReimbursement: generalData.overseasSupport || 0,
					wearSponsoredHat: generalData.sponsorCap || false,
					prizeType:
						generalData.incentiveTaxOption === 'amountDisplayed'
							? 'displayed'
							: 'actual',
					incentives: (generalData.incentiveSettings || []).map((incentive) => ({
						startRank: incentive.start,
						endRank: incentive.end === 'CUT' ? 0 : incentive.end,
						percentage: incentive.percentage,
					})),
				}
			}
		}

		if (informationForDisplay.isDomesticContract) {
			// 국내 계약
			const domesticData = domesticFormRef.current?.getFormData?.()
			if (domesticData) {
				contract.domesticContract = {
					isSalaryTaxIncluded: domesticData.applyTax === 'include',
					payrollCycles: 'weekly',
					weeklySalaryCutThrough: Number(domesticData.cutPass || 0),
					weeklySalaryCutFail: Number(domesticData.cutOut || 0),
					jejuTournamentParticipation: domesticData.isTourJejudo || false,
					expenseReimbursementJeju: domesticData.isTourJejudo
						? Number(domesticData.jejudoSupport || 0)
						: 0,
					wearSponsoredHat: domesticData.sponsorCap || false,
					prizeType:
						domesticData.incentiveTaxOption === 'amountDisplayed'
							? 'displayed'
							: 'actual',
					incentives: (domesticData.incentiveSettings || []).map((incentive) => ({
						startRank: incentive.start,
						endRank: incentive.end === 'CUT' ? 0 : incentive.end,
						percentage: incentive.percentage,
					})),
				}
			}
		}

		if (informationForDisplay.isOverseasContract) {
			// 해외 계약
			const overseasData = overseasFormRef.current?.getFormData?.()
			if (overseasData) {
				contract.overseasContract = {
					isSalaryTaxIncluded: overseasData.applyTax === 'include',
					payrollCycles: 'weekly',
					weeklySalaryCutThrough: Number(overseasData.cutPass || 0),
					weeklySalaryCutFail: Number(overseasData.cutOut || 0),
					wearSponsoredHat: overseasData.sponsorCap || false,
					expenseReimbursement: overseasData.overseasSupport || 0,
					prizeType:
						overseasData.incentiveTaxOption === 'amountDisplayed'
							? 'displayed'
							: 'actual',
					incentives: (overseasData.incentiveSettings || []).map((incentive) => ({
						startRank: incentive.start,
						endRank: incentive.end === 'CUT' ? 0 : incentive.end,
						percentage: incentive.percentage,
					})),
				}
			}
		}

		console.log('contract : ', contract)
		return contract
	}

	return (
		<div className={styles.jobPostThirdPageContainer}>
			<header className={styles.pageHeader}>
				<h1 className={styles.title}>제안하기</h1>
				<button
					type="button"
					className={styles.closeButton}
					onClick={() => {
						actions.updateStore({ offerInput: storeInitState.offerInput })
						navigate('/search')
					}}
					aria-label="닫기">
					<XMarkIcon className={styles.closeIcon} />
				</button>
			</header>
			<div className={styles.progressBar}></div>
			<div className={styles.tourInformationDisplayWrapper}>
				<span className={styles.tourInformationDisplayWrapperTitle}>
					계약 기간 및 대회정보를 확인해주세요
				</span>
				<div className={styles.tourInformationContainer}>
					{currentUser && (
						<>
							{userAssociation && (
								<span className={styles.userAssociation}>{userAssociation}</span>
							)}
							{informationDisplayOption === 'period-no-tour-last-half' && (
								<>
									<div>
										<span className={styles.contractType}>기간계약</span>
										<span className={styles.contractTypeDetail}>
											(하반기,대회미정)
										</span>
									</div>
									<span className={styles.contractPeriod}>
										{`계약일 ~ ${informationForDisplay.currentYear}-12-31`}
										<InformationCircleIcon
											className={styles.noticeIcon}
											data-tooltip-id="my-tooltip4"
											data-tooltip-content={`계약기간은 공식연습일과 예비일 등이 포함될 수 있습니다.`}
										/>
										<Tooltip
											id="my-tooltip4"
											place="top"
											className={styles.tooltip}
										/>
									</span>
								</>
							)}
							{informationDisplayOption === 'period-no-tour-first-half' && (
								<>
									<div>
										<span className={styles.contractType}>기간계약</span>
										<span className={styles.contractTypeDetail}>
											(상반기,대회미정)
										</span>
									</div>
									<span className={styles.contractPeriod}>
										{`계약일 ~ ${informationForDisplay.currentYear}-06-30`}
										<InformationCircleIcon
											className={styles.noticeIcon}
											data-tooltip-id="my-tooltip5"
											data-tooltip-content={`계약기간은 공식연습일과 예비일 등이 포함될 수 있습니다.`}
										/>
										<Tooltip
											id="my-tooltip5"
											place="top"
											className={styles.tooltip}
										/>
									</span>
								</>
							)}
							{informationDisplayOption === 'period-no-tour-first-half-last-half' && (
								<>
									<div>
										<span className={styles.contractType}>기간계약</span>
										<span className={styles.contractTypeDetail}>
											(상반기,하반기,대회미정)
										</span>
									</div>
									<span className={styles.contractPeriod}>
										{`계약일 ~ ${informationForDisplay.currentYear}-12-31`}
										<InformationCircleIcon
											className={styles.noticeIcon}
											data-tooltip-id="my-tooltip6"
											data-tooltip-content={`계약기간은 공식연습일과 예비일 등이 포함될 수 있습니다.`}
										/>
										<Tooltip
											id="my-tooltip6"
											place="top"
											className={styles.tooltip}
										/>
									</span>
								</>
							)}
							{informationDisplayOption === 'period-tour-selected' &&
								informationForDisplay.selectedToursData &&
								informationForDisplay.selectedToursData.length > 0 && (
									<>
										<div>
											<span className={styles.contractType}>기간계약</span>
											<span className={styles.contractTypeDetail}>
												(
												{offerPeriodType &&
												offerPeriodType.includes('first-half') &&
												offerPeriodType.includes('last-half')
													? '상반기,하반기, '
													: offerPeriodType &&
														  offerPeriodType.includes('first-half')
														? '상반기, '
														: '하반기, '}
												총 {informationForDisplay.selectedToursData.length}
												경기)
											</span>
										</div>
										<span className={styles.contractPeriod}>
											{informationForDisplay.selectedToursData[0].startDate} ~{' '}
											{
												informationForDisplay.selectedToursData[
													informationForDisplay.selectedToursData.length -
														1
												].endDate
											}
											<InformationCircleIcon
												className={styles.noticeIcon}
												data-tooltip-id="my-tooltip7"
												data-tooltip-content={`계약기간은 공식연습일과 예비일 등이 포함될 수 있습니다.`}
											/>
											<Tooltip
												id="my-tooltip7"
												place="top"
												className={styles.tooltip}
											/>
										</span>
										<div className={styles.tourDisplayContainer}>
											{informationForDisplay.selectedToursData.map((tour) => (
												<div
													key={tour.id}
													className={styles.tourDisplayItem}>
													<span className={styles.tourRegion}>
														{tour.region === 'DOMESTIC'
															? '국내'
															: '해외'}
													</span>
													<span className={styles.tourName}>
														{tour.name}
													</span>
												</div>
											))}
										</div>
									</>
								)}
							{informationDisplayOption === 'short-contract' &&
								informationForDisplay.selectedToursData &&
								informationForDisplay.selectedToursData.length > 0 && (
									<>
										<div>
											<span className={styles.contractType}>단기계약</span>
											<span className={styles.contractTypeDetail}>
												(총 1경기)
											</span>
										</div>
										<span className={styles.contractPeriod}>
											{`${informationForDisplay.selectedToursData[0].startDate} ~ ${informationForDisplay.selectedToursData[0].endDate}`}
											<InformationCircleIcon
												className={styles.noticeIcon}
												data-tooltip-id="my-tooltip8"
												data-tooltip-content={`계약기간은 공식연습일과 예비일 등이 포함될 수 있습니다.`}
											/>
											<Tooltip
												id="my-tooltip8"
												place="top"
												className={styles.tooltip}
											/>
										</span>
										<div className={styles.tourDisplayContainer}>
											{informationForDisplay.selectedToursData.map((tour) => (
												<div
													key={tour.id}
													className={styles.tourDisplayItem}>
													<span className={styles.tourRegion}>
														{tour.region === 'DOMESTIC'
															? '국내'
															: '해외'}
													</span>
													<span className={styles.tourName}>
														{tour.name}
													</span>
												</div>
											))}
										</div>
									</>
								)}
							{informationDisplayOption === 'long-contract' &&
								informationForDisplay.selectedToursData &&
								informationForDisplay.selectedToursData.length > 0 && (
									<>
										<div>
											<span className={styles.contractType}>장기계약</span>
											<span className={styles.contractTypeDetail}>
												(총 {informationForDisplay.selectedToursData.length}
												경기)
											</span>
										</div>
										<span className={styles.contractPeriod}>
											{`${informationForDisplay.selectedToursData[0].startDate} ~ ${informationForDisplay.selectedToursData[informationForDisplay.selectedToursData.length - 1].endDate}`}
											<InformationCircleIcon
												className={styles.noticeIcon}
												data-tooltip-id="my-tooltip9"
												data-tooltip-content={`계약기간은 공식연습일과 예비일 등이 포함될 수 있습니다.`}
											/>
											<Tooltip
												id="my-tooltip9"
												place="top"
												className={styles.tooltip}
											/>
										</span>
										<div className={styles.tourDisplayContainer}>
											{informationForDisplay.selectedToursData.map((tour) => (
												<div
													key={tour.id}
													className={styles.tourDisplayItem}>
													<span className={styles.tourRegion}>
														{tour.region === 'DOMESTIC'
															? '국내'
															: '해외'}
													</span>
													<span className={styles.tourName}>
														{tour.name}
													</span>
												</div>
											))}
										</div>
									</>
								)}
						</>
					)}
				</div>
				{informationForDisplay.isGeneralContract && (
					<GeneralContractInfoSubmitForm
						ref={generalFormRef}
						onValidationChange={handleValidationChange}
						isOverseas={informationForDisplay.isOverseasContract}
					/>
				)}
				{!informationForDisplay.isGeneralContract &&
					informationForDisplay.isDomesticContract && (
						<DomesticContractInfoSubmitForm
							ref={domesticFormRef}
							onValidationChange={handleValidationChange}
							hasDomesticPreferredContract={hasDomesticPreferredContract}
							domesticContractData={preferredContract?.domesticContract}
						/>
					)}
				{!informationForDisplay.isGeneralContract &&
					informationForDisplay.isOverseasContract && (
						<OverseasContractInfoSubmitForm
							ref={overseasFormRef}
							onValidationChange={handleValidationChange}
							hasOverSeasPreferredContract={hasOverSeasPreferredContract}
							overseasContractData={preferredContract?.overseasContract}
						/>
					)}
			</div>

			{/* 통합 버튼 */}
			{(informationForDisplay.isGeneralContract ||
				informationForDisplay.isDomesticContract ||
				informationForDisplay.isOverseasContract) && (
				<div className={styles.whatsNext}>
					<div className={styles.whatsNextButtonContainer}>
						<button
							type="button"
							onClick={() => {
								// 폼 데이터를 contract DTO 형식으로 변환
								const contractData = prepareDataForSubmit()
								console.log('다음 버튼 클릭 - 변환된 계약 데이터:', contractData)

								// store에 DTO 형식으로 저장
								const payload = {
									offerInput: {
										...state.offerInput,
										contract: contractData,
									},
								}
								actions.updateStore(payload)

								navigate(`/offer/create/confirm?by=${userType}`)
							}}
							className={
								(() => {
									console.log('Button validation check:', {
										isGeneralContract: informationForDisplay.isGeneralContract,
										isDomesticContract:
											informationForDisplay.isDomesticContract,
										isOverseasContract:
											informationForDisplay.isOverseasContract,
										formValidations,
									})

									// 일반 계약인 경우
									if (informationForDisplay.isGeneralContract) {
										console.log(
											'General contract validation:',
											formValidations.general,
										)
										return formValidations.general
									}
									// 국내만 있는 경우
									if (
										informationForDisplay.isDomesticContract &&
										!informationForDisplay.isOverseasContract
									) {
										console.log(
											'Domestic only validation:',
											formValidations.domestic,
										)
										return formValidations.domestic
									}
									// 해외만 있는 경우
									if (
										informationForDisplay.isOverseasContract &&
										!informationForDisplay.isDomesticContract
									) {
										console.log(
											'Overseas only validation:',
											formValidations.overseas,
										)
										return formValidations.overseas
									}
									// 국내와 해외가 모두 있는 경우 둘 다 통과해야 함
									if (
										informationForDisplay.isDomesticContract &&
										informationForDisplay.isOverseasContract
									) {
										const bothValid =
											formValidations.domestic && formValidations.overseas
										console.log('Both contracts validation:', {
											domestic: formValidations.domestic,
											overseas: formValidations.overseas,
											bothValid,
										})
										return bothValid
									}
									console.log('No matching condition, returning false')
									return false
								})()
									? styles.nextButton
									: styles.nextButtonDisabled
							}>
							다음
						</button>
					</div>
				</div>
			)}
		</div>
	)
}
export default OfferThirdPage
